import moment from "moment";
import { defineStore } from "pinia";

import { ModuleStatus } from "@/models/ModuleStatus";

import { isApiError } from "@/services/Api";
import { filterSetService } from "@/services";

import { useBrandStore } from "@/stores/brand-store";
import { useLoggingStore } from "@/stores/logging-store";
import { useCaseFileStore } from "@/stores/case-file-store";

import type { TagFilter } from "@/models/Tag";
import type FilterSet from "@/models/FilterSet";
import type AgreementPeriod from "@/models/AgreementPeriod";
import type { Filter, DateRangeFilter } from "@/models/Filter";

const defaultFilter = Object.freeze({
  hideDuplicates: true,
  hideDrafts: false,
  classifications: ["Any"],
  tags: [],
  dateRange: {
    from: "",
    fromDate: moment(),
    to: "",
    toDate: moment(),
    includeBlanks: true,
  },
  currentFolder: "",
  groupByFolders: false,
  adviceInstanceId: "",
  evidenceCounts: ["Any"],
  agreementPeriod: undefined,
  summariserStatuses: ["Any"],
});
interface State {
  moduleStatus: ModuleStatus;
  filter: Filter;
  filterSets: FilterSet[];
  selectedFilterSet: FilterSet | null;
}
export const useFiltersStore = defineStore("filters", {
  scope: "global",
  state: (): State => ({
    moduleStatus: ModuleStatus.Initial,
    filter: { ...defaultFilter },
    filterSets: [],
    selectedFilterSet: null,
  }),
  getters: {},
  actions: {
    async load(payload?: { isRefresh?: boolean }): Promise<void> {
      const { selectedClient, selectedBrand } = useBrandStore();
      if (selectedClient == null || selectedBrand == null) return;

      if (this.moduleStatus !== ModuleStatus.Initial && !payload?.isRefresh) {
        return;
      }

      this.setStatus(ModuleStatus.Loading);

      // Try and load the filter sets
      try {
        // The api catches errors and logs them, so we don't need to do anything here
        const filterSets: FilterSet[] = await filterSetService.getAll(selectedClient, selectedBrand);

        this.setFilterSets(filterSets);
      } catch (error) {
        if (isApiError(error) && error.status === 204) {
          this.setFilterSets(this.filterSets);
          return;
        }
        useLoggingStore().Error({
          message: "Failed to load user session into module",
          data: error as object,
        });
      } finally {
        this.setStatus(ModuleStatus.Ready);
      }
    },
    changeCurrentFolder(payload: { currentFolder: string }): void {
      this.currentFolderChanged(payload.currentFolder);
    },
    changeGroupByFolders(payload: { groupByFolders: boolean }): void {
      this.groupByFoldersChanged(payload.groupByFolders);
    },
    changeDuplicateValue(payload: { hideDuplicates: boolean }): void {
      this.duplicateValueChanged(payload);
    },
    changeDraftValue(payload: { hideDrafts: boolean }): void {
      this.draftValueChanged(payload);
    },
    changeAdviceInstanceId(payload: { instanceReference: string }): void {
      this.adviceInstanceIdChanged(payload.instanceReference);
    },
    addTag(payload: { tagType: string; tag: string }): void {
      const tagFilter: TagFilter = this.filter.tags.find((f) => f.tagType === payload.tagType) ?? {
        tagType: payload.tagType,
        includeBlanks: false,
        tags: [],
      };

      if (tagFilter.tags.includes(payload.tag)) {
        return;
      }

      // If we are using, any then we can just remove all the others
      if (payload.tag === "Any") {
        tagFilter.tags = [];
      }

      tagFilter.tags.push(payload.tag);

      this.tagsChanged(tagFilter);
    },
    removeTag(payload: { tagType: string; tag: string }): void {
      const tagFilter: TagFilter = this.filter.tags.find((f) => f.tagType === payload.tagType) ?? {
        tagType: payload.tagType,
        includeBlanks: false,
        tags: [],
      };

      if (!tagFilter.tags.includes(payload.tag)) {
        return;
      }

      tagFilter.tags = tagFilter.tags.filter((f) => f !== payload.tag);

      this.tagsChanged(tagFilter);
    },
    includeBlanksForTagType(payload: { tagType: string; includeBlanks: boolean }): void {
      const tagFilter: TagFilter = this.filter.tags.find((f) => f.tagType === payload.tagType) ?? {
        tagType: payload.tagType,
        includeBlanks: false,
        tags: [],
      };

      tagFilter.includeBlanks = payload.includeBlanks;

      this.tagsChanged(tagFilter);
    },
    changeClassifications(payload: { classifications: string[] }) {
      this.classificationsChanged(payload.classifications);
    },
    changeDateRange(payload: { from: string; to: string; includeBlanks: boolean }) {
      // Need to change the date range filter into a valid regex:

      const dateRangeFilter: DateRangeFilter = { ...payload };

      this.dateRangeChanged(dateRangeFilter);
    },
    changeEvidenceCounts(payload: { evidenceCounts: string[] }) {
      this.evidenceCountsChanged(payload.evidenceCounts || []);
    },
    clearEvidenceCounts() {
      this.evidenceCountsChanged([]);
    },
    addAgreementPeriod(payload: AgreementPeriod) {
      this.updateAgreementPeriod(payload);
    },
    changeSummariserStatuses(payload: { statuses: string[] }) {
      this.summariserStatusesChanged(payload.statuses);
    },
    removeAgreementPeriod() {
      this.updateAgreementPeriod(undefined);
    },
    selectFilterSet(name?: string) {
      const set = this.filterSets.find((f) => f.name === name);
      this.selectedFilterSetChanged(set);
      if (!set) {
        this.reset();
        return;
      }
      this.filterChanged({ ...set.value });
    },
    deleteFilterSet(name: string) {
      const set = this.filterSets.filter((f) => f.name !== name);
      this.setFilterSets(set);
    },
    // Mutations
    duplicateValueChanged(payload: { hideDuplicates: boolean }) {
      this.filter.hideDuplicates = String(payload.hideDuplicates) === "true";
    },
    draftValueChanged(payload: { hideDrafts: boolean }) {
      this.filter.hideDrafts = String(payload.hideDrafts) === "true";
    },
    tagsChanged(tagFilter: TagFilter) {
      this.filter.tags = this.filter.tags.filter((f) => f.tagType !== tagFilter.tagType);
      this.filter.tags.push(tagFilter);
    },
    filterChanged(payload: Filter) {
      this.filter = payload;
    },
    dateRangeChanged(payload: DateRangeFilter) {
      this.filter.dateRange = payload;
    },
    classificationsChanged(payload: string[]): void {
      this.filter.classifications = payload;
    },
    currentFolderChanged(payload: string): void {
      this.filter.currentFolder = payload;
    },
    groupByFoldersChanged(payload: boolean): void {
      this.filter.groupByFolders = payload;

      if (this.filter.groupByFolders) {
        const file = useCaseFileStore().currentFile;
        if (file != null) this.currentFolderChanged(file.folder);
      }
    },
    adviceInstanceIdChanged(payload: string): void {
      this.filter.adviceInstanceId = payload;
    },
    evidenceCountsChanged(payload: string[]) {
      this.filter.evidenceCounts = payload;
    },
    updateAgreementPeriod(payload: AgreementPeriod | undefined): void {
      this.filter.agreementPeriod = payload;
    },
    reset(): void {
      this.filter = { ...defaultFilter };
      this.resetTriggered();
    },
    summariserStatusesChanged(statuses: string[]): void {
      this.filter.summariserStatuses = statuses;
    },
    setStatus(moduleStatus: ModuleStatus): void {
      this.moduleStatus = moduleStatus;
    },
    setFilterSets(filterSets: FilterSet[]): void {
      this.filterSets = filterSets;
    },
    selectedFilterSetChanged(filterSet: FilterSet | null = null): void {
      this.selectedFilterSet = filterSet;
    },
    resetTriggered() {
      // Used to watch in other components
    },
  },
});
